import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/lib/api'
import type { User } from '@/types/api'

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean

  // Actions
  login: (telegramData: string) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
  updateUser: (userData: Partial<User>) => void
  checkAuth: () => Promise<boolean>
  setLoading: (loading: boolean) => void
  setAuth: (token: string, user: User) => void
  resetAuth: () => void
}

// 用戶主動登出的標記（session 範圍）：
// 存在時 TelegramLogin 不做自動登入，避免「登出後馬上被自動登回來」
export const MANUAL_LOGOUT_FLAG = 'slipin.manualLogout'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (telegramData: string) => {
        console.log('Auth Store: Starting login process')
        set({ isLoading: true })
        try {
          console.log('Auth Store: Calling API telegramLogin')
          const response = await apiClient.telegramLogin(telegramData)
          
          console.log('Auth Store: API response received:', response)
          
          // Store token to localStorage
          localStorage.setItem('access_token', response.token)
          console.log('Auth Store: Token stored in localStorage')
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
          })
          
          console.log('Auth Store: State updated, isAuthenticated: true')
        } catch (error) {
          console.error('Auth Store: Login error:', error)
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await apiClient.logout()
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          localStorage.removeItem('access_token')
          localStorage.removeItem('token_expires_at')
          // 主動登出後需要用戶手動確認才再登入（防止 Mini App 自動回登）
          sessionStorage.setItem(MANUAL_LOGOUT_FLAG, '1')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      // 憑證失效時的靜默重置（不設主動登出標記，Mini App 可無縫自動重新登入）
      resetAuth: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      refreshToken: async () => {
        try {
          const response = await apiClient.refreshToken()
          localStorage.setItem('access_token', response.token)
          
          set({
            token: response.token,
            isAuthenticated: true,
          })
          
          return true
        } catch (error) {
          console.error('Token refresh failed:', error)
          get().logout()
          return false
        }
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }))
      },

      checkAuth: async () => {
        const token = localStorage.getItem('access_token')
        if (!token) {
          set({ isAuthenticated: false })
          return false
        }

        try {
          const user = await apiClient.getProfile()
          set({
            user,
            token,
            isAuthenticated: true,
          })
          return true
        } catch (error) {
          console.error('Auth check failed:', error)
          localStorage.removeItem('access_token')
          localStorage.removeItem('token_expires_at')
          set({ user: null, token: null, isAuthenticated: false })
          return false
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      setAuth: (token: string, user: User) => {
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)